import React, { createContext, useContext, useEffect, useState } from 'react';
import type { PublicUser } from '@tucupet/shared';
import { api, ApiError } from './api';

interface AuthCtx {
  me: PublicUser | null;
  loading: boolean;
  reload: () => Promise<void>;
  logout: () => Promise<void>;
  /** True when the user is admin OR has the given permission. */
  can: (perm: string) => boolean;
}

const Ctx = createContext<AuthCtx>({ me: null, loading: true, reload: async () => {}, logout: async () => {}, can: () => false });

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const u = await api<PublicUser>('/api/auth/me');
      setMe(u);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else setMe(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api('/api/auth/logout', { body: {} }).catch(() => {});
    setMe(null);
  };

  useEffect(() => {
    reload();
    const onExpired = () => setMe(null);
    window.addEventListener('tc:session-expired', onExpired);
    return () => window.removeEventListener('tc:session-expired', onExpired);
  }, []);

  const can = (perm: string): boolean => {
    if (!me) return false;
    if (me.role === 'admin') return true;
    return (me.permissions ?? []).includes(perm);
  };

  return <Ctx.Provider value={{ me, loading, reload, logout, can }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
